import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, string } from 'prop-types'

import {
  FULL_PRODUCT_NOT_IN_ASSORTMENT,
  FULL_PRODUCT_STATUS_AVAILABLE,
  FULL_PRODUCT_STATUS_AVAILABLE_REPLACEMENT,
  FULL_PRODUCT_STATUS_CURRENTLY_NOT_AVAILABLE,
  FULL_PRODUCT_STATUS_DELIVERY_10,
  FULL_PRODUCT_STATUS_DELIVERY_20,
  FULL_PRODUCT_STATUS_DELIVERY_3,
  FULL_PRODUCT_STATUS_DELIVERY_5,
  FULL_PRODUCT_STATUS_DELIVERY_5_7,
  FULL_PRODUCT_STATUS_DELIVERY_7,
  FULL_PRODUCT_STATUS_GRATIS,
  FULL_PRODUCT_STATUS_NOT_AVAILABLE,
  FULL_PRODUCT_STATUS_NOT_IN_CATALOG,
  FULL_PRODUCT_STATUS_OUT_OF_TRADE,
  FULL_PRODUCT_STATUS_UNAVAILABLE,
} from 'shared/consts/stockStatus'
import { isRebrandingActive } from 'shared/experiments/utils/rebranding'
import IconErrorTriangle from 'views/components/atoms/Icon/IconErrorTriangle'
import IconTick from 'views/components/atoms/Icon/IconTick'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import {
  getFullStatus,
  getStatusBgColor,
  getStatusColor,
  getStatusColorRebranding,
} from 'views/utils/productStatusUtils'
import { ProductType } from 'views/utils/propTypes'
import { pickRequired } from 'views/utils/propTypeUtils'

import './ProductStatus.scss'

const ProductStatus = props => {
  const {
    stockStatus: { statusColor, statusReason, notOnStockMessage },
    dot,
  } = props
  const {
    publicRuntimeConfig: {
      pageProperties: { experiments },
    },
  } = useShopAndRuntimeConfig()
  const isRebrandingEnabled = isRebrandingActive(experiments)

  const className = classNames(
    'u-font-weight--bold u-no-margin o-ProductStatus',
    getStatusColor(statusColor),
    getStatusColorRebranding(statusColor),
    props.className
  )
  const fullReason = getFullStatus(statusReason, notOnStockMessage)
  const style = { color: '#ff8a00', fontWeight: 'bold' }

  const dotComponent = dot ? (
    <span
      className={`o-ProductStatus--dot u-display--inline-block u-margin-x-small--right ${getStatusBgColor(
        statusColor
      )}`}
      style={statusColor === 'orange' ? { backgroundColor: '#ff8a00' } : {}}
    />
  ) : null

  const iconForDefaultCase =
    statusColor === 'green' ? (
      <IconTick size={20} />
    ) : (
      <IconErrorTriangle size={20} />
    )

  switch (fullReason) {
    case FULL_PRODUCT_STATUS_AVAILABLE:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {isRebrandingEnabled ? <IconTick size={20} /> : dotComponent}
          <span
            className={classNames({
              'u-font-weight--bold': isRebrandingEnabled,
            })}
          >
            <Trans id="general.label.product.available" />
          </span>
        </p>
      )
    case FULL_PRODUCT_STATUS_DELIVERY_3:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.delivery.3_5" />
        </p>
      )
    case FULL_PRODUCT_STATUS_DELIVERY_5:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.delivery.5" />
        </p>
      )
    case FULL_PRODUCT_STATUS_DELIVERY_5_7:
      return (
        <p
          className={className}
          data-qa-id="product-status-qa-id"
          style={style}
        >
          {dotComponent}
          <Trans id="general.label.product.delivery.5_7" />
        </p>
      )
    case FULL_PRODUCT_STATUS_DELIVERY_7:
      return (
        <p
          className={className}
          data-qa-id="product-status-qa-id"
          style={style}
        >
          {dotComponent}
          <Trans id="general.label.product.delivery.7_10" />
        </p>
      )
    case FULL_PRODUCT_STATUS_DELIVERY_10:
      return (
        <p
          className={className}
          data-qa-id="product-status-qa-id"
          style={style}
        >
          {dotComponent}
          <Trans id="general.label.product.delivery.10_14" />
        </p>
      )
    case FULL_PRODUCT_STATUS_DELIVERY_20:
      return (
        <p
          className={className}
          data-qa-id="product-status-qa-id"
          style={style}
        >
          {dotComponent}
          <Trans id="general.label.product.delivery.20_30" />
        </p>
      )
    case FULL_PRODUCT_STATUS_OUT_OF_TRADE:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.outOfTrade" />
        </p>
      )
    case FULL_PRODUCT_STATUS_CURRENTLY_NOT_AVAILABLE:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {isRebrandingEnabled ? <IconErrorTriangle size={20} /> : dotComponent}
          <Trans id="general.label.product.currentlyNotAvailable" />
        </p>
      )
    case FULL_PRODUCT_STATUS_NOT_AVAILABLE:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {isRebrandingEnabled ? <IconErrorTriangle size={20} /> : dotComponent}
          <Trans id="general.label.product.notAvailable" />
        </p>
      )
    case FULL_PRODUCT_STATUS_NOT_IN_CATALOG:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.notInCatalog" />
        </p>
      )
    case FULL_PRODUCT_STATUS_UNAVAILABLE:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {isRebrandingEnabled ? <IconErrorTriangle size={20} /> : dotComponent}
          <Trans id="general.label.product.unavailable" />
        </p>
      )
    case FULL_PRODUCT_STATUS_AVAILABLE_REPLACEMENT:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.availableReplacement" />
        </p>
      )
    case FULL_PRODUCT_STATUS_GRATIS:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.gratis" />
        </p>
      )
    case FULL_PRODUCT_NOT_IN_ASSORTMENT:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {dotComponent}
          <Trans id="general.label.product.notInAssortment" />
        </p>
      )
    default:
      return (
        <p className={className} data-qa-id="product-status-qa-id">
          {isRebrandingEnabled ? iconForDefaultCase : dotComponent}
          {fullReason}
        </p>
      )
  }
}

ProductStatus.propTypes = {
  ...pickRequired(ProductType, ['stockStatus']),
  dot: bool,
  className: string,
}

ProductStatus.defaultProps = {
  dot: false,
}

export default ProductStatus
